import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/apiClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { addPendingOperation } from "@/lib/offlineStorage";
import { addProcessSchema } from "@/schemas/workOrderSchemas";

interface AddProcessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrderId: string;
}

export function AddProcessDialog({
  open,
  onOpenChange,
  workOrderId,
}: AddProcessDialogProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const [loading, setLoading] = useState(false);
  const [selectedProcessId, setSelectedProcessId] = useState("");
  const [selectedOperatorId, setSelectedOperatorId] = useState("");
  const [selectedTagUid, setSelectedTagUid] = useState("");
  const [notes, setNotes] = useState("");
  const [processData, setProcessData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: processes } = useQuery({
    queryKey: ["manufacturing-processes"],
    queryFn: async () => {
      const { data, error } = await api
        .from("manufacturing_processes")
        .select("*")
        .order("sequence_order");

      if (error) throw error;
      return data;
    },
  });

  const { data: operators } = useQuery({
    queryKey: ["operators"],
    queryFn: async () => {
      const { data, error } = await api
        .from("profiles")
        .select("id, full_name, employee_id")
        .order("full_name");

      if (error) throw error;
      return data;
    },
  });

  const { data: availableTags } = useQuery({
    queryKey: ["process-nfc-tags", open],
    queryFn: async () => {
      const { data, error } = await api
        .from("nfc_tags")
        .select("tag_uid, tag_label")
        .eq("status", "active")
        .is("work_order_id", null)
        .is("process_id", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  const selectedProcess = processes?.find((p) => p.id === selectedProcessId);
  const requiredFields =
    (selectedProcess?.requires_data as any)?.fields || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setErrors({});
    
    // Validate form data
    const validation = addProcessSchema.safeParse({
      processId: selectedProcessId,
      operatorId: selectedOperatorId,
      notes,
      processData,
    });
    
    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0].toString()] = err.message;
        }
      });
      setErrors(fieldErrors);
      toast.error("Por favor corrija los errores en el formulario");
      return;
    }

    setLoading(true);
    try {
      // operator_id is required - use current user if not selected
      const operatorId = validation.data.operatorId || user.id;

      // process_data should be null if empty, not {}
      const processDataValue = validation.data.processData &&
        Object.keys(validation.data.processData).length > 0
          ? validation.data.processData
          : null;

      const processRecordData = {
        work_order_id: workOrderId,
        process_id: validation.data.processId,
        operator_id: operatorId,
        status: "pending",
        notes: validation.data.notes || null,
        process_data: processDataValue,
      };

      if (isOnline) {
        const { error } = await api.from("process_records").insert(processRecordData);
        if (error) throw error;
        if (selectedTagUid) {
          const { error: tagError } = await api
            .from("nfc_tags")
            .update({
              work_order_id: workOrderId,
              process_id: validation.data.processId,
              tag_type: "process",
              assigned_at: new Date().toISOString(),
              assigned_by: user.id,
            })
            .eq("tag_uid", selectedTagUid);
          if (tagError) throw tagError;
        }
        toast.success(t.common.success);
      } else {
        addPendingOperation({
          type: "process_record",
          action: "create",
          data: processRecordData,
        });
        toast.success("Proceso guardado localmente. Se sincronizará al reconectar.");
      }

      queryClient.invalidateQueries({
        queryKey: ["process-records", workOrderId],
      });
      onOpenChange(false);
      setSelectedProcessId("");
      setSelectedOperatorId("");
      setSelectedTagUid("");
      setNotes("");
      setProcessData({});
      setErrors({});
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t.workOrders.addProcess}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t.workOrders.selectProcess} *</Label>
            <Select value={selectedProcessId} onValueChange={setSelectedProcessId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {processes?.map((process) => (
                  <SelectItem key={process.id} value={process.id}>
                    {t.processes[process.name as keyof typeof t.processes]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.processId && (
              <p className="text-sm text-destructive mt-1">{errors.processId}</p>
            )}
          </div>

          <div>
            <Label>Operario (opcional)</Label>
            <Select
              value={selectedOperatorId || "none"}
              onValueChange={(value) =>
                setSelectedOperatorId(value === "none" ? "" : value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Asignar operario (opcional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin asignar</SelectItem>
                {operators?.map((operator) => (
                  <SelectItem key={operator.id} value={operator.id}>
                    {operator.full_name}
                    {operator.employee_id && ` (${operator.employee_id})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Etiqueta NFC (opcional)</Label>
            <Select
              value={selectedTagUid || "none"}
              onValueChange={(value) =>
                setSelectedTagUid(value === "none" ? "" : value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Asignar etiqueta NFC (opcional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin asignar</SelectItem>
                {availableTags?.filter(t => t.tag_uid).map((tag: any) => (
                  <SelectItem key={tag.tag_uid} value={tag.tag_uid}>
                    {tag.tag_label ? `${tag.tag_label} (${tag.tag_uid})` : tag.tag_uid}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {requiredFields.length > 0 && (
            <div className="space-y-3 border-t pt-4">
              <Label>{t.workOrders.processData}</Label>
              {requiredFields.map((field: string) => (
                <div key={field}>
                  <Label htmlFor={field} className="text-sm">
                    {t.processFields[field as keyof typeof t.processFields]}
                  </Label>
                  <Input
                    id={field}
                    value={processData[field] || ""}
                    onChange={(e) =>
                      setProcessData({ ...processData, [field]: e.target.value })
                    }
                    maxLength={500}
                  />
                  {errors[`processData.${field}`] && (
                    <p className="text-sm text-destructive mt-1">
                      {errors[`processData.${field}`]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          <div>
            <Label>{t.workOrders.notes}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={1000}
            />
            {errors.notes && (
              <p className="text-sm text-destructive mt-1">{errors.notes}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {notes.length}/1000 caracteres
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t.common.cancel}
            </Button>
            <Button
              type="submit"
              disabled={loading || !selectedProcessId}
            >
              {loading ? t.common.loading : t.workOrders.addProcess}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
